import React, {useCallback, useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {
  copyText,
  goBack,
  goCS,
  goTo,
  goWhatsAppChat,
  toPriceStr,
} from '@/utils';
import {getCommissionRate} from './proxy.service';
import {
  defaultHeaderImg,
  icBgImage,
  ruleBgImage,
  vip1Image,
  vip2Image,
  vip3Image,
  vip4Image,
} from './proxy.variable';
import {
  ArrowRightIcon,
  CopyIcon,
  HomeActiveIcon,
  HomeBettingIcon,
  HomeBetUserIcon,
  HomeCommissionIcon,
  HomeDown,
  HomeNewUserIcon,
  HomeRechargeIcon,
  HomeRight,
  HomeUp,
  RefreshIcon,
} from './svg.variable';
import './proxy-h5.css';

type TierRateItem = {
  gameIcon: string;
  gameName: string;
  tier1: string;
  tier2: string;
  tier3: string;
  tier4: string;
};

export type ProxyListResult<T> = {
  totalPages: number;
  totalSize: number;
  content: T[];
};

export const levelOptions: Record<number, string> = {
  1: vip1Image,
  2: vip2Image,
  3: vip3Image,
  4: vip4Image,
};

export function getLevelImage(level?: number) {
  return levelOptions[level || 1] || vip1Image;
}

export function formatProxyDate(date?: string) {
  if (!date) {
    return '-';
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD/MM YYYY') : date;
}

export function toApiDate(date?: string | null) {
  if (!date) {
    return undefined;
  }
  return dayjs(date).format('YYYYMMDD');
}

export function toInputDate(date?: string | null) {
  if (!date) {
    return '';
  }
  return dayjs(date).format('YYYY-MM-DD');
}

export function rangeStr(min: string, max: string, defaultVal?: string) {
  if (+(min || 0) === 0 || +(max || 0) === 0) {
    return `${(+(defaultVal || 0) * 100).toFixed(2)}%`;
  }
  if (+(min || 0) === +(max || 0)) {
    return `${(+(min || 0) * 100).toFixed(2)}%`;
  }
  return `${(+(min || 0) * 100).toFixed(2)}% ~ ${(+(max || 0) * 100).toFixed(
    2,
  )}%`;
}

export function ProxyH5Header({
  title,
  onBack,
  service = true,
  right,
  light = false,
  className,
  titleClassName,
  leftClassName,
  rightClassName,
  backButtonClassName,
}: {
  title: string;
  onBack?: () => void;
  service?: boolean;
  right?: React.ReactNode;
  light?: boolean;
  className?: string;
  titleClassName?: string;
  leftClassName?: string;
  rightClassName?: string;
  backButtonClassName?: string;
}) {
  return (
    <div
      className={clsx(
        'sticky top-0 z-30 flex h-11 flex-none items-center',
        light
          ? 'bg-transparent text-white'
          : 'bg-white text-[var(--proxy-main)] shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)]',
        className,
      )}>
      <div
        className={clsx(
          'absolute left-0 top-0 flex h-full items-center pl-3',
          leftClassName,
        )}>
        <button
          type="button"
          className={clsx(
            'mr-1 flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0',
            backButtonClassName,
          )}
          onClick={onBack || goBack}>
          <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
              d="M10.5 2L5 7.99676L10.5 14"
              stroke={light ? '#FFFFFF' : '#1F2329'}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div
        className={clsx(
          'flex flex-1 items-center justify-center text-base font-bold',
          titleClassName,
        )}>
        {title}
      </div>
      <div
        className={clsx(
          'absolute right-0 top-0 flex h-full items-center pr-3',
          rightClassName,
        )}>
        {right}
        {!right && service && (
          <button
            type="button"
            className="border-0 bg-transparent p-0"
            onClick={goCS}>
            <img
              src={require('@components/assets/icons/headphone.webp')}
              alt="service"
              className="h-6 w-6"
            />
          </button>
        )}
      </div>
    </div>
  );
}

export function ProxyCard({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx('rounded-lg bg-white proxy-h5-shadow', className)}
      {...rest}>
      {children}
    </div>
  );
}

export function ProxySectionTitle({
  title,
  right,
}: {
  title: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center">
        <div className="proxy-h5-gradient h-4 w-1 rounded-sm" />
        <span className="ml-2 text-sm font-bold text-[var(--proxy-main)]">
          {title}
        </span>
      </div>
      {right}
    </div>
  );
}

export function ProxyNavRow({
  icon,
  title,
  onClick,
}: {
  icon: string;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex h-10 w-full items-center justify-between border-0 bg-transparent p-0 text-left"
      onClick={onClick}>
      <div className="flex items-center">
        <img src={icon} alt="" className="h-4 w-4" />
        <span className="ml-3 text-sm text-[var(--proxy-second)]">{title}</span>
      </div>
      <svg
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5 shrink-0">
        <path
          d="M5.25 10.5L8.75 7L5.25 3.5"
          stroke="#9FA5AC"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export function ProxySearchInput({
  value,
  onChange,
  placeholder,
  onSearch,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  onSearch?: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#9fa6ac]">
          ID
        </span>
        <input
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          className="h-10 w-full rounded border border-[#e8eaee] bg-[#f3f5f8] pl-9 pr-3 text-sm outline-none"
        />
      </div>
      {onSearch && (
        <button
          type="button"
          className="h-10 rounded bg-[var(--proxy-primary)] px-4 text-sm font-bold text-white disabled:opacity-50"
          disabled={!value}
          onClick={onSearch}>
          Search
        </button>
      )}
    </div>
  );
}

export function ProxyEmpty({
  title,
  actionLabel,
  onAction,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg bg-white px-6 py-10 text-center">
      <img
        src={defaultHeaderImg}
        alt=""
        className="mb-3 h-12 w-12 rounded-full opacity-50"
      />
      <div className="text-sm text-[var(--proxy-text)]">{title}</div>
      {actionLabel && onAction && (
        <button
          type="button"
          className="mt-4 rounded-full bg-[var(--proxy-primary)] px-5 py-2 text-sm font-bold text-white"
          onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export function AmountChange({amount}: {amount?: string | number}) {
  const numericAmount = +(amount || 0);
  const positive = numericAmount > 0;
  const Icon = positive ? HomeUp : HomeDown;

  return (
    <div
      className={clsx(
        'mt-0.5 flex items-center text-[10px] font-bold leading-3',
        positive ? 'text-[#009B22]' : 'text-[var(--proxy-muted)]',
      )}>
      <span>{toPriceStr(Math.abs(numericAmount))}</span>
      {positive ? (
        <Icon className="ml-0.5 h-3 w-3" />
      ) : (
        <span className="ml-1">-</span>
      )}
    </div>
  );
}

export function ProxyCopyBlock({
  code,
  onRefresh,
  onCopy,
  onShare,
  title,
  resetLabel,
  shareLabel,
}: {
  code: string;
  onRefresh: () => void;
  onCopy: () => void;
  onShare: () => void;
  title: string;
  resetLabel: string;
  shareLabel: string;
}) {
  return (
    <ProxyCard className="p-3">
      <ProxySectionTitle
        title={title}
        right={
          <button
            type="button"
            className="flex h-7 items-center gap-1 rounded bg-[#fff3eb] px-2 text-xs font-bold text-[var(--proxy-primary)]"
            onClick={onRefresh}>
            <RefreshIcon className="h-3.5 w-3.5" />
            {resetLabel}
          </button>
        }
      />
      <div
        className="proxy-h5-copy-bg mt-3 flex h-[5.25rem] overflow-hidden rounded"
        style={{backgroundImage: `url(${icBgImage})`}}>
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex gap-2 text-lg font-bold text-[var(--proxy-main)]">
            {(code || '--------').split('').map((item, index) => (
              <span key={`${item}-${index}`}>{item}</span>
            ))}
          </div>
          <span className="mt-1 text-xs text-[var(--proxy-text)]">{title}</span>
        </div>
        <button
          type="button"
          className="flex w-24 items-center justify-center border-0 bg-transparent text-base font-bold text-[var(--proxy-primary)]"
          onClick={onCopy}>
          Copy
        </button>
      </div>
      <button
        type="button"
        className="mt-3 h-12 w-full rounded-full bg-[var(--proxy-main)] text-sm font-bold text-white"
        onClick={onShare}>
        {shareLabel}
      </button>
    </ProxyCard>
  );
}

export function useInfinitePager<T>(
  request: (pageNo: number, pageSize: number) => Promise<ProxyListResult<T>>,
  pageSize = 20,
) {
  const [items, setItems] = useState<T[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadPage = useCallback(
    async (targetPage: number, replace = false) => {
      if (loadingRef.current || loadingMoreRef.current) {
        return;
      }
      if (targetPage === 1) {
        loadingRef.current = true;
        setLoading(true);
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }
      try {
        const result = await request(targetPage, pageSize);
        setTotalPages(result.totalPages || 1);
        setTotalSize(result.totalSize || 0);
        setPageNo(targetPage);
        setItems(prev =>
          replace || targetPage === 1
            ? result.content
            : [...prev, ...result.content],
        );
      } finally {
        if (targetPage === 1) {
          loadingRef.current = false;
          setLoading(false);
        } else {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      }
    },
    [pageSize, request],
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) {
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (
          entry.isIntersecting &&
          !loadingMore &&
          !loading &&
          pageNo < totalPages
        ) {
          loadPage(pageNo + 1);
        }
      });
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadPage, loading, loadingMore, pageNo, totalPages]);

  return {
    items,
    totalSize,
    loading,
    loadingMore,
    isEnd: pageNo >= totalPages,
    reload: () => loadPage(1, true),
    sentinelRef,
  };
}

export function ProxyCommissionRateModal({
  open,
  onClose,
  level,
  onInvite,
}: {
  open: boolean;
  onClose: () => void;
  level: number;
  onInvite?: () => void;
}) {
  const {t} = useTranslation();
  const [data, setData] = useState<TierRateItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    getCommissionRate({level})
      .then((response: any) => setData((response || []) as TierRateItem[]))
      .finally(() => setLoading(false));
  }, [level, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/40"
      onClick={onClose}>
      <div
        className="max-h-[85vh] w-full rounded-t-2xl bg-white p-5"
        onClick={event => event.stopPropagation()}>
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-[#d9d9d9]" />
        <div className="proxy-h5-rate-header mb-3 rounded-xl px-3 py-3">
          <img
            src={getLevelImage(level)}
            alt=""
            className="h-5 w-[3.1875rem]"
          />
          <div className="mt-2 text-sm font-bold text-[var(--proxy-main)]">
            {t('proxy.home.rebate')}
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-[var(--proxy-line)]">
          <div className="flex bg-[#f3f5f8] text-xs font-bold text-[var(--proxy-main)]">
            <div className="w-32 px-3 py-2">
              {t('proxy.commission-detail.type')}
            </div>
            <div className="grid flex-1 grid-cols-4 px-2 py-2 text-center">
              {[1, 2, 3, 4].map(item => (
                <div key={item}>{`Tier ${item}`}</div>
              ))}
            </div>
          </div>
          <div className="proxy-h5-scroll max-h-[45vh] overflow-auto">
            {loading && (
              <div className="p-4 text-center text-sm text-[var(--proxy-text)]">
                Loading...
              </div>
            )}
            {!loading &&
              data.map((item, index) => (
                <div
                  key={`${item.gameName}-${index}`}
                  className={clsx(
                    'flex text-xs text-[var(--proxy-text)]',
                    index % 2 === 0 ? 'bg-white' : 'bg-[#f8fafc]',
                  )}>
                  <div className="flex w-32 items-center gap-2 px-3 py-2">
                    <img
                      src={item.gameIcon}
                      alt={item.gameName}
                      className="h-5 w-5 rounded"
                    />
                    <span className="line-clamp-2">{item.gameName}</span>
                  </div>
                  <div className="grid flex-1 grid-cols-4 px-2 py-2 text-center">
                    {[item.tier1, item.tier2, item.tier3, item.tier4].map(
                      (rate, rateIndex) => (
                        <div key={`${item.gameName}-${rateIndex}`}>
                          {(+rate * 100).toFixed(2)}%
                        </div>
                      ),
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
        {onInvite && (
          <button
            type="button"
            className="mt-4 h-12 w-full rounded-full bg-[var(--proxy-main)] text-sm font-bold text-white"
            onClick={onInvite}>
            {level < 4
              ? t('proxy.home.invitation-link')
              : t('proxy.home.invitation-link')}
          </button>
        )}
      </div>
    </div>
  );
}

export function OnlineLabel({text}: {text?: string}) {
  const {t} = useTranslation();
  return (
    <div className="flex items-center gap-1 text-xs text-[var(--proxy-text)]">
      <HomeActiveIcon className="h-3.5 w-3.5" />
      <span>{text || t('proxy.new-user.online')}</span>
    </div>
  );
}

export function LastActiveLabel({text}: {text?: string}) {
  return (
    <div className="flex items-center gap-1 text-xs text-[var(--proxy-text)]">
      <HomeBetUserIcon className="h-3.5 w-3.5" />
      <span>{text || '-'}</span>
    </div>
  );
}

export function useCopy(text: string) {
  return useCallback(() => {
    copyText(text);
  }, [text]);
}

export {goBack, goCS, goTo, goWhatsAppChat, icBgImage, ruleBgImage, toPriceStr};

export {
  ArrowRightIcon,
  CopyIcon,
  HomeActiveIcon,
  HomeBettingIcon,
  HomeBetUserIcon,
  HomeCommissionIcon,
  HomeNewUserIcon,
  HomeRechargeIcon,
  HomeRight,
};
